import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, BookOpen, AlertTriangle, Phone, Shield, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOMAIN_NAMES = {
  constitution: 'Constitution de la RDC',
  famille: 'Code de la Famille',
  penal: 'Code Penal',
  travail: 'Code du Travail',
  investissement: 'Code des Investissements',
  numerique: 'Code du Numerique',
  procedure_penale: 'Code de Procedure Penale',
  protection_enfant: "Loi sur la Protection de l'Enfant",
  foncier: 'Code Foncier et Immobilier',
  entrepreneuriat: "Loi sur l'Entrepreneuriat et les Startups",
  baux_loyers: 'Baux à Loyers',
  obligations: 'Code des Contrats et Obligations',
  barreau: 'Organisation du Barreau',
  minier: 'Code Minier',
  procedure_civile: 'Code de Procédure Civile',
};

// Contacts d'urgence affiches uniquement pour la Loi sur la protection
// de l'enfant. Base legale : Art. 74 (organes de protection) et Art. 77
// (Brigade speciale de protection de l'enfant) de la Loi n° 09/001.
const CHILD_PROTECTION_EMERGENCY = [
  {
    icon: Phone,
    label: 'Ligne verte Enfance RDC',
    value: '117',
    href: 'tel:117',
    hint: 'Signalement de maltraitance, exploitation ou disparition',
    testid: 'emergency-line-117',
  },
  {
    icon: Shield,
    label: 'Brigade spéciale de protection de l\'enfant',
    value: 'Commissariat le plus proche',
    href: null,
    hint: 'Rendez-vous au commissariat le plus proche — la BSPE relève de la Police (Art. 77 de la loi)',
    testid: 'emergency-bspe',
  },
  {
    icon: Mail,
    label: 'Signalement au Parquet',
    value: 'Officier du Ministère Public',
    href: null,
    hint: 'Toute personne a l\'obligation de dénoncer les violences faites à un enfant (Art. 192)',
    testid: 'emergency-parquet',
  },
];

const DocumentExplorer = () => {
  const { domain } = useParams();
  const navigate = useNavigate();
  const openKey = `mibeko_explorer_open_${domain}`;
  const scrollKey = `mibeko_explorer_scroll_${domain}`;
  const readOpen = () => {
    try { return JSON.parse(sessionStorage.getItem(openKey) || '{}'); } catch { return {}; }
  };
  const [hierarchy, setHierarchy] = useState({});
  const [loading, setLoading] = useState(true);
  const [openBooks, setOpenBooks] = useState(() => readOpen().books || {});
  const [openTitres, setOpenTitres] = useState(() => readOpen().titres || {});
  const [openChapitres, setOpenChapitres] = useState(() => readOpen().chapitres || {});
  const [domainInfo, setDomainInfo] = useState(null);

  // Recharge l'etat sauvegarde quand on change de code (le composant n'est pas
  // remonte entre deux domaines : useState lazy ne suffit pas).
  useEffect(() => {
    const s = readOpen();
    setOpenBooks(s.books || {});
    setOpenTitres(s.titres || {});
    setOpenChapitres(s.chapitres || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  // Persiste l'etat des onglets ouverts pour ne pas perdre sa place au retour.
  useEffect(() => {
    try {
      sessionStorage.setItem(openKey, JSON.stringify({
        books: openBooks, titres: openTitres, chapitres: openChapitres,
      }));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openBooks, openTitres, openChapitres, openKey]);

  const getScroller = () => document.querySelector('.app-content');

  const saveScroll = () => {
    const el = getScroller();
    const y = (el && el.scrollTop) || window.scrollY || 0;
    try { sessionStorage.setItem(scrollKey, String(y)); } catch {}
  };

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/hierarchy/${domain}`).then(r => r.json()),
      fetch(`${API_URL}/api/domains/${domain}`).then(r => r.json()),
    ]).then(([hierData, domData]) => {
      setHierarchy(hierData.hierarchy || {});
      setDomainInfo(domData);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [domain]);

  // Restaure la position de lecture une fois la hierarchie affichee.
  useEffect(() => {
    if (loading) return;
    let y = 0;
    try { y = parseInt(sessionStorage.getItem(scrollKey) || '0', 10) || 0; } catch {}
    if (!y) return;
    requestAnimationFrame(() => {
      const el = getScroller();
      if (el) el.scrollTop = y;
      else window.scrollTo(0, y);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const toggle = (setter, key) => {
    setter(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const totalArticles = domainInfo?.total_articles || 0;

  return (
    <div>
      <div className="back-header">
        <button className="back-btn" onClick={() => navigate(-1)} data-testid="back-btn-explorer">
          <ArrowLeft />
        </button>
        <h1>{DOMAIN_NAMES[domain] || domainInfo?.name || domain}</h1>
      </div>

      <div className="blue-header" data-testid="explorer-header">
        <h1>{DOMAIN_NAMES[domain] || domainInfo?.name || domain}</h1>
        <p>{domainInfo?.description || ''}</p>
        <div className="text-count">{totalArticles} articles</div>
      </div>

      {loading ? (
        <div className="loading-spinner" />
      ) : (
        <div className="page-container">
          {domain === 'protection_enfant' && (
            <section
              className="emergency-panel"
              data-testid="child-emergency-panel"
              aria-label="Contacts d'urgence — Protection de l'enfant"
            >
              <div className="emergency-header">
                <AlertTriangle style={{ width: 18, height: 18 }} />
                <div>
                  <div className="emergency-title">Urgence — Protection de l&apos;enfant</div>
                  <div className="emergency-sub">
                    Un enfant est en danger&nbsp;? Signalez immédiatement.
                  </div>
                </div>
              </div>
              <ul className="emergency-list">
                {CHILD_PROTECTION_EMERGENCY.map((c) => {
                  const Icon = c.icon;
                  const inner = (
                    <>
                      <span className="emergency-icon"><Icon /></span>
                      <span className="emergency-body">
                        <span className="emergency-label">{c.label}</span>
                        <span className="emergency-value">{c.value}</span>
                        <span className="emergency-hint">{c.hint}</span>
                      </span>
                    </>
                  );
                  return c.href ? (
                    <li key={c.testid}>
                      <a href={c.href} className="emergency-row" data-testid={c.testid}>
                        {inner}
                      </a>
                    </li>
                  ) : (
                    <li key={c.testid}>
                      <div className="emergency-row" data-testid={c.testid}>{inner}</div>
                    </li>
                  );
                })}
              </ul>
              <p className="emergency-footnote">
                Base légale&nbsp;: articles 74, 77 et 192 de la Loi n° 09/001 du 10 janvier 2009
                portant protection de l&apos;enfant.
              </p>
            </section>
          )}
          {Object.entries(hierarchy).map(([livre, titres]) => (
            <div className="hierarchy-item" key={livre} data-testid={`livre-${livre.substring(0, 20)}`}>
              <button
                className={`hierarchy-toggle ${openBooks[livre] ? 'open' : ''}`}
                onClick={() => toggle(setOpenBooks, livre)}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BookOpen style={{ width: '16px', height: '16px', color: 'var(--primary)' }} />
                  {livre}
                </span>
                <ChevronRight />
              </button>
              {openBooks[livre] && (
                <div className="hierarchy-children">
                  {Object.entries(titres).map(([titre, chapitres]) => (
                    <div key={titre} style={{ marginBottom: '6px' }}>
                      <button
                        className={`hierarchy-toggle ${openTitres[titre] ? 'open' : ''}`}
                        onClick={() => toggle(setOpenTitres, titre)}
                        style={{ fontSize: '13px', padding: '10px 14px', background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)' }}
                      >
                        <span>{titre}</span>
                        <ChevronRight />
                      </button>
                      {openTitres[titre] && (
                        <div style={{ paddingLeft: '12px', paddingTop: '4px' }}>
                          {Object.entries(chapitres).map(([chapitre, articles]) => (
                            <div key={chapitre}>
                              {chapitre !== 'Articles' && (
                                <button
                                  className={`hierarchy-toggle ${openChapitres[chapitre] ? 'open' : ''}`}
                                  onClick={() => toggle(setOpenChapitres, chapitre)}
                                  style={{ fontSize: '12px', padding: '8px 12px', background: 'transparent', color: 'var(--text-gray)' }}
                                >
                                  <span>{chapitre}</span>
                                  <ChevronRight />
                                </button>
                              )}
                              {(chapitre === 'Articles' || openChapitres[chapitre]) && (
                                <div style={{ paddingLeft: chapitre === 'Articles' ? '0' : '8px', paddingTop: '4px' }}>
                                  {articles.map(article => (
                                    <Link
                                      key={article.id}
                                      to={`/article/${article.id}`}
                                      className="hierarchy-article"
                                      data-testid={`article-link-${article.id}`}
                                      onClick={saveScroll}
                                    >
                                      <div className="article-num">{article.article_number}</div>
                                      <div className="article-summary">{article.summary?.substring(0, 80)}...</div>
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentExplorer;
